import { useState, useCallback } from "react";
import { IconCamera, IconPlus, IconChevronRight, IconCheck, IconStack2, IconLoader2 } from "@tabler/icons-react";
import { LoadingImage } from "@/components/loading-image";
import { cn } from "@/lib/utils";
import type { PhotoCategory } from "@/api/hooks/use-photo-categories";
import type { BuildPhoto } from "@/api/hooks/use-build-photos";
import { flattenCategories } from "@/lib/photo-categories";
import type { PhotoCategoryId } from "@/types/identifiers";

function ProgressBar({ value, max, size = "full" }: { value: number; max: number; size?: "full" | "compact" }) {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = percent >= 100 ? "bg-green-500" : percent > 0 ? "bg-amber-500" : "bg-muted";
  return (
    <div className={cn(
      "h-1 bg-muted/50 rounded-full overflow-hidden",
      size === "compact" ? "w-16" : "w-full"
    )}>
      <div
        className={cn("h-full rounded-full transition-all", color)}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

const STORAGE_KEY = "photo-grid-collapsed";

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveCollapsed(set: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
}

interface PhotoCategoryGridProps {
  sections: PhotoCategory[];
  photosByCategoryId: Map<PhotoCategoryId, BuildPhoto[]>;
  onSlotClick?: (categoryId: PhotoCategoryId) => void;
  uploadingCategoryIds?: Set<PhotoCategoryId>;
}

function PhotoSlot({
  photo,
  alt,
  onClick,
  isUploading,
}: {
  photo: BuildPhoto;
  alt: string;
  onClick?: () => void;
  isUploading?: boolean;
}) {
  if (isUploading) {
    return (
      <div className="aspect-[4/3] rounded-lg border border-border flex items-center justify-center bg-muted/30">
        <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
      </div>
    );
  }
  const src = photo.thumbnail_url ?? photo.image_url;
  if (!src) return null;
  const fullSrc = photo.image_url;
  const img = (
    <LoadingImage
      src={src}
      alt={alt}
      className="aspect-[4/3] w-full object-cover rounded-lg border border-border"
      skeletonClassName="aspect-[4/3] rounded-lg"
    />
  );
  if (onClick) {
    return (
      <button onClick={onClick} className="text-left">
        {img}
      </button>
    );
  }
  return fullSrc ? (
    <a href={fullSrc} target="_blank" rel="noopener noreferrer">
      {img}
    </a>
  ) : (
    img
  );
}

function EmptySlot({
  label,
  onClick,
  icon: Icon = IconCamera,
  showMultiBadge = false,
  isUploading = false,
}: {
  label: string;
  onClick?: () => void;
  icon?: typeof IconCamera;
  showMultiBadge?: boolean;
  isUploading?: boolean;
}) {
  if (isUploading) {
    return (
      <div className="aspect-[4/3] rounded-lg border border-border flex items-center justify-center bg-muted/30">
        <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
      </div>
    );
  }

  const badge = showMultiBadge && (
    <div className="absolute top-1 right-1 h-4 w-4 rounded bg-blue-500/90 flex items-center justify-center" title="Multiple photos allowed">
      <IconStack2 className="h-2.5 w-2.5 text-white" />
    </div>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="relative aspect-[4/3] rounded-lg border-2 border-dashed border-muted-foreground/20 hover:border-muted-foreground/40 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground/50 hover:text-muted-foreground/70"
      >
        {badge}
        <Icon className="h-4 w-4" />
        <span className="text-[10px] leading-tight text-center px-1">{label}</span>
      </button>
    );
  }
  return (
    <div className="aspect-[4/3] rounded-lg border-2 border-dashed border-muted-foreground/20 flex flex-col items-center justify-center gap-1 text-muted-foreground/40">
      <Icon className="h-4 w-4" />
      <span className="text-[10px] leading-tight text-center px-1">No photo</span>
    </div>
  );
}

export function PhotoCategoryGrid({
  sections,
  photosByCategoryId,
  onSlotClick,
  uploadingCategoryIds,
}: PhotoCategoryGridProps) {
  const [collapsed, setCollapsed] = useState(loadCollapsed);

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsed(next);
      return next;
    });
  }, []);

  return (
    <div className="space-y-2">
      {sections.map((section) => {
        const leaves = flattenCategories([section]);
        const filledCount = leaves.reduce(
          (sum, l) => sum + (photosByCategoryId.get(l.id)?.length ?? 0),
          0,
        );
        const totalSlots = leaves.length;
        const isFull = filledCount >= totalSlots;
        const isCollapsed = collapsed.has(section.id);
        const hasPhotos = filledCount > 0;

        const borderColor = isFull
          ? "border-l-green-500"
          : hasPhotos
            ? "border-l-amber-500"
            : "border-l-border";

        return (
          <div
            key={section.id}
            className={cn(
              "transition-all bg-card border-l-3",
              borderColor,
              isCollapsed ? "pl-4 pr-5 py-3 rounded-r-lg" : "pl-4 pr-5 py-4"
            )}
          >
            <button
              onClick={() => toggle(section.id)}
              className="flex items-center gap-2 w-full text-left"
            >
              <IconChevronRight
                className={cn(
                  "h-3.5 w-3.5 text-muted-foreground transition-transform",
                  !isCollapsed && "rotate-90"
                )}
              />
              <h3 className={cn(
                "text-[13px] flex-1",
                hasPhotos ? "font-medium" : "font-normal text-muted-foreground"
              )}>
                {section.name}
              </h3>
              <ProgressBar value={filledCount} max={totalSlots} size="compact" />
              <span className={cn(
                "text-xs tabular-nums",
                hasPhotos ? "text-foreground" : "text-muted-foreground/50"
              )}>
                {filledCount}/{totalSlots}
              </span>
              {isFull && <IconCheck className="h-3.5 w-3.5 text-emerald-500" />}
            </button>

            {!isCollapsed && (
              <>
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {leaves.map((cat) => {
                    const photos = photosByCategoryId.get(cat.id) ?? [];
                    const isUploading = uploadingCategoryIds?.has(cat.id) ?? false;
                    if (cat.allow_multiple) {
                      return (
                        <MultiCategorySlots
                          key={cat.id}
                          category={cat}
                          photos={photos}
                          onSlotClick={onSlotClick}
                          isUploading={isUploading}
                        />
                      );
                    }
                    const photo = photos[0];
                    return (
                      <div key={cat.id} className="flex flex-col gap-1">
                        {photo ? (
                          <PhotoSlot
                            photo={photo}
                            alt={cat.name}
                            onClick={onSlotClick ? () => onSlotClick(cat.id) : undefined}
                            isUploading={isUploading}
                          />
                        ) : (
                          <EmptySlot
                            label={cat.name}
                            onClick={onSlotClick ? () => onSlotClick(cat.id) : undefined}
                            isUploading={isUploading}
                          />
                        )}
                        {photo && !isUploading && (
                          <span className="text-[10px] text-muted-foreground truncate">{cat.name}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MultiCategorySlots({
  category,
  photos,
  onSlotClick,
  isUploading = false,
}: {
  category: PhotoCategory;
  photos: BuildPhoto[];
  onSlotClick?: (categoryId: PhotoCategoryId) => void;
  isUploading?: boolean;
}) {
  return (
    <>
      {photos.map((photo) => (
        <div key={photo.id} className="flex flex-col gap-1">
          <PhotoSlot
            photo={photo}
            alt={category.name}
            onClick={onSlotClick ? () => onSlotClick(category.id) : undefined}
          />
          <span className="text-[10px] text-muted-foreground truncate">{category.name}</span>
        </div>
      ))}
      {onSlotClick && (
        <div className="flex flex-col gap-1">
          <EmptySlot
            label={photos.length === 0 ? category.name : "Add more"}
            onClick={() => onSlotClick(category.id)}
            icon={photos.length === 0 ? IconCamera : IconPlus}
            showMultiBadge={photos.length === 0}
            isUploading={isUploading}
          />
        </div>
      )}
    </>
  );
}
